import { Box, Button, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";

import MuiTable from "../MuiTable/MuiTable";
import {
    hasAccess,
    httpRequest,
    onRequestError,
    printLog,
} from "../../helper/global";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import { PERMISSIONS } from "../../helper/constant";

export default function EducationalMaterials({
    theme,
    user,
    permissionConfig,
    syncEducationalMaterials = false,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [educationalMaterials, setEducationalMaterials] = useState([]);

    const columns = [
        {
            id: "name",
            label: "File Name",
            sort: true,
            visible: true,
        },

        {
            id: "folder",
            label: "Folder",
            sort: true,
            visible: true,
        },

        {
            id: "",
            label: "Actions",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                const buttons = [];

                buttons.push(
                    <IconButton
                        aria-label="open"
                        onClick={(event) => {
                            event.stopPropagation();

                            //open to new tab
                            window.open(row.url, "_blank");
                        }}
                    >
                        <OpenInNewIcon />
                    </IconButton>,
                );

                buttons.push(
                    <IconButton
                        aria-label="copy"
                        onClick={(event) => {
                            event.stopPropagation();
                            navigator.clipboard.writeText(row.url);
                        }}
                        title="Copy URL to clipboard"
                    >
                        <ContentCopyIcon />
                    </IconButton>,
                );

                return buttons;
            },
        },
    ];

    const handleEducationalMaterialsResults = (result) => {
        if (result === false) {
            return;
        }

        setEducationalMaterials(result);
    };

    const CACHE_KEY = "educational_materials_cache";
    const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

    const fetchEducationalMaterials = async () => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { data, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < CACHE_TTL_MS) {
                    handleEducationalMaterialsResults(data);
                    return;
                }
            }

            const result = await httpRequest(
                `utility/educational-materials`,
                {},
                "GET",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            if (result === false) {
                return;
            }

            localStorage.setItem(
                CACHE_KEY,
                JSON.stringify({ data: result.payload, timestamp: Date.now() }),
            );
            handleEducationalMaterialsResults(result.payload);
        } catch (error) {
            console.error("Error fetching email templates:", error);
        }
    };

    const handleSyncEducationalMaterials = async () => {
        try {
            dispatch(
                showLoader({
                    message: "Syncing educational materials...",
                }),
            );
            const result = await httpRequest(
                `utility/sync-education-drive`,
                {},
                "GET",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                    dispatch(hideLoader());
                },
            );

            if (result === false) {
                return;
            }
            dispatch(hideLoader());

            localStorage.removeItem(CACHE_KEY);
            handleEducationalMaterialsResults(result.payload);
        } catch (error) {
            console.error("Error syncing educational materials:", error);
        }
    };

    useEffect(() => {
        fetchEducationalMaterials();
    }, []);

    return (
        <>
            <Box sx={{ p: 3 }}>
                <Box>
                    {syncEducationalMaterials && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                handleSyncEducationalMaterials();
                            }}
                        >
                            Sync Educational Materials
                        </Button>
                    )}
                    <Box sx={{ mt: 3 }}>
                        <MuiTable
                            allowSearch={false}
                            theme={theme}
                            id={"materialId"}
                            headCells={columns}
                            defaultData={educationalMaterials}
                            rowsPerPageArray={[100, 200, 500]}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    );
}
